import { AccordionDetails } from "@mui/material";
import create from "zustand";
// utils
import axios from "../utils/axios";
import { isValidToken, setSession, getTokenData } from "../utils/jwt";

// ----------------------------------------------------------------------
const token = localStorage.getItem("accessToken");

const getCurrentRole = (accessToken = token) => {
  if (isValidToken(accessToken)) {
    const tokenData = getTokenData(accessToken);
    return tokenData.role;
  }

  return "USER";
};

const getCurrentCountry = (accessToken = token) => {
  if (isValidToken(accessToken)) {
    const tokenData = getTokenData(accessToken);
    return tokenData.country;
  }

  return "England";
};

const initialState = {
  isAuthenticated: isValidToken(token),
  authToken: isValidToken(token) ? token : null,
  isInitialized: true,
  user: isValidToken(token) ? getTokenData(token) : {},
  method: "jwt",
  role: getCurrentRole(),
  country: getCurrentCountry(),
};

const login = async (email, password) => {
  const response = await axios.post("/auth/login", {
    email,
    password,
  });

  const { user, accessToken, refreshToken } = response.data;

  setSession(accessToken, refreshToken);

  return { user, accessToken, refreshToken };
};

const loginGuest = async () => {
  const response = await axios.post("/auth/login-guest");

  const { user, accessToken, refreshToken } = response.data;

  setSession(accessToken, refreshToken);

  return { user, accessToken, refreshToken };
};

const logout = async () => {
  const response = await axios.post("/auth/logout");

  setSession(null);

  return response;
};

const register = async (
  name,
  surname,
  email,
  password,
  password_confirmation,
  city,
  country,
  company,
  province,
  merchant_category
) => {
  const response = await axios.post("/auth/register", {
    name,
    surname,
    email,
    password,
    password_confirmation,
    city,
    country,
    company,
    province,
    merchant_category,
  });

  const { accessToken, refreshToken, user } = response.data;

  setSession(accessToken, refreshToken);

  return { user, accessToken, refreshToken };
};

const updateProfile = async (
  name,
  surname,
  email,
  city,
  country,
  province,
  merchant_category,
  company
) => {
  const response = await axios.post("/auth/profile/update", {
    name,
    surname,
    email,
    city,
    country,
    province,
    merchant_category,
    company,
  });

  const { user, message } = response.data;

  return { user, message };
};

const verifyOtp = async (otpCode) => {
  const response = await axios.post("/otp/verify", { otp_code: otpCode });

  const { token: accessToken, user, refreshToken } = response.data;

  setSession(accessToken, refreshToken);

  return { accessToken, user, refreshToken };
};

const profile = async () => {
  const response = await axios.post("/auth/profile", {});

  const user = response.data;

  return { user };
};

const useStore = create((set) => ({
  ...initialState,
  isWebVersion: () => {
    // return true; // NOTE: REMOVE THIS LINE AFTER 3D DEV PHASE [fbca4f5c-662d-494e-9a7a-e557279c5690]
    const host = window.location.host;
    const domains = host.split(".");

    if (domains[0] === "web") {
      return true;
    }

    return false;
  },
  getUserId: () => {
    const token = localStorage.getItem("accessToken");
    if (isValidToken(token)) {
      const tokenData = getTokenData(token);

      return tokenData.user_id;
    }

    return null;
  },
  getSpecialAdmin: () => {
    const token = localStorage.getItem("accessToken");
    if (isValidToken(token)) {
      const tokenData = getTokenData(token);

      return tokenData.specialAdmin;
    }

    return null;
  },
  login: async (email, password) => {
    const { user, accessToken } = await login(email, password);
    set({
      isAuthenticated: isValidToken(accessToken),
      user,
      role: getCurrentRole(accessToken),
      country: getCurrentCountry(accessToken),
    });
  },
  loginGuest: async () => {
    const { user, accessToken } = await loginGuest();
    set({
      isAuthenticated: isValidToken(accessToken),
      user,
      role: getCurrentRole(accessToken),
    });
  },
  logout: async () => {
    await logout();
    set({ isAuthenticated: false });
  },
  register: async (
    name,
    surname,
    email,
    password,
    password_confirmation,
    city,
    country,
    company,
    province,
    merchant_category
  ) => {
    const { user, accessToken, refreshToken } = await register(
      name,
      surname,
      email,
      password,
      password_confirmation,
      city,
      country,
      company,
      province,
      merchant_category
    );

    set({
      isAuthenticated: isValidToken(accessToken),
      user,
      role: getCurrentRole(accessToken),
      country: getCurrentCountry(accessToken),
    });
  },
  updateProfile: async (
    name,
    surname,
    email,
    city,
    country,
    province,
    merchant_category,
    company
  ) => {
    const { user, message } = await updateProfile(
      name,
      surname,
      email,
      city,
      country,
      province,
      merchant_category,
      company
    );

    set({ user });

    return { user, message };
  },
  profile: async () => {
    const { user } = await profile();
    set({ user });
    return user;
  },
  verifyOtp: async (otpCode) => {
    const { accessToken, refreshToken } = await verifyOtp(otpCode);

    const { role } = getTokenData(accessToken);

    const { user } = await profile();

    set({
      isAuthenticated: isValidToken(accessToken),
      user,
      role,
    });
  },
}));

export default useStore;
